// Formulaire de création / édition d'une publication éditoriale
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { VOIES, PLATEFORMES, STATUTS_PUBLICATION } from "@/lib/constants";
import { aujourdHuiISO } from "@/lib/dates";

export const PostDialog = ({ open, onClose, post, defaultDate, users, onSaved }) => {
  const [form, setForm] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (post) {
      const [date, heure] = (post.date_publication || "").split("T");
      setForm({ ...post, date, heure: heure || "12:00" });
    } else {
      setForm({
        titre: "", voie: "quotidien", plateformes: [], responsable_id: "", statut: "idee",
        date: defaultDate || aujourdHuiISO(), heure: "12:00", notes: "", lien_assets: "",
      });
    }
  }, [open, post, defaultDate]);

  if (!form) return null;

  const set = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));
  const basculerPlateforme = (p) =>
    set("plateformes", form.plateformes.includes(p) ? form.plateformes.filter((x) => x !== p) : [...form.plateformes, p]);

  const sauver = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    const payload = {
      titre: form.titre, voie: form.voie, plateformes: form.plateformes,
      responsable_id: form.responsable_id || null, statut: form.statut,
      date_publication: `${form.date}T${form.heure}`, notes: form.notes, lien_assets: form.lien_assets,
    };
    try {
      if (post) {
        await api.patch(`/editorial-posts/${post.id}`, payload);
        toast.success("Publication mise à jour");
      } else {
        await api.post("/editorial-posts", payload);
        toast.success("Publication créée");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async () => {
    try {
      await api.delete(`/editorial-posts/${post.id}`);
      toast.success("Publication supprimée");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{post ? "Modifier la publication" : "Nouvelle publication"}</DialogTitle>
          <DialogDescription>Voie éditoriale, plateforme, date et statut de la publication.</DialogDescription>
        </DialogHeader>
        <form onSubmit={sauver} className="space-y-4">
          <div>
            <Label>Titre *</Label>
            <Input required value={form.titre} onChange={(e) => set("titre", e.target.value)} className="rounded-xl mt-1.5" data-testid="post-titre-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Voie éditoriale</Label>
              <Select value={form.voie} onValueChange={(v) => set("voie", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="post-voie-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VOIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.couleur }} />
                        {v.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="post-statut-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUTS_PUBLICATION).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Plateformes</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {Object.entries(PLATEFORMES).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 transition-colors">
                  <Checkbox checked={form.plateformes.includes(k)} onCheckedChange={() => basculerPlateforme(k)} data-testid={`post-plateforme-${k}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Responsable</Label>
              <Select value={form.responsable_id || "aucun"} onValueChange={(v) => set("responsable_id", v === "aucun" ? "" : v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="post-responsable-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucun">— Aucun —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Date</Label>
                <Input type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl mt-1.5" data-testid="post-date-input" />
              </div>
              <div>
                <Label>Heure</Label>
                <Input type="time" required value={form.heure} onChange={(e) => set("heure", e.target.value)} className="rounded-xl mt-1.5" data-testid="post-heure-input" />
              </div>
            </div>
          </div>
          <div>
            <Label>Notes / brief</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-xl mt-1.5" data-testid="post-notes-input" />
          </div>
          <div>
            <Label>Lien vers les assets (URL)</Label>
            <Input type="url" placeholder="https://…" value={form.lien_assets} onChange={(e) => set("lien_assets", e.target.value)} className="rounded-xl mt-1.5" data-testid="post-assets-input" />
          </div>
          <DialogFooter className="gap-2">
            {post && (
              <Button type="button" variant="ghost" onClick={supprimer} className="text-destructive gap-1.5 rounded-full mr-auto" data-testid="post-delete-button">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
            <Button type="submit" disabled={envoi} className="rounded-full" data-testid="post-save-button">
              {post ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
